@file:Suppress("MagicNumber", "ReturnCount")

package top.wcpe.allincore.ui.runtime.animation

import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.ui.runtime.element.ProgressBar
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.math.mathRoundCompat
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.ir.prop.PropAppliers
import kotlin.jvm.JvmStatic

/**
 * 动画计算引擎：负责动画节点的实际插值与属性应用。仅由 [AnimationScheduler] 内部调用。
 */
internal object AnimationEngine {

    private val colorProps: MutableSet<String> = linkedSetOf("background", "color")

    // ===== 节点分发 =====

    /** 根据动画节点类型分发到对应的处理方法。 */
    @JvmStatic
    fun applyNode(
        activeAnimation: AnimationScheduler.ActiveAnimation,
        node: UiAnimation,
        elapsedMs: Double,
        startValues: MutableMap<String, Any?>,
    ) {
        val local = elapsedMs - node.delayMs
        if (local < 0.0) return
        when (node.kind) {
            UiAnimation.Kind.TRANSITION -> applyTransition(activeAnimation, node, local, startValues)
            UiAnimation.Kind.KEYFRAME -> applyKeyframes(activeAnimation, node, local)
            UiAnimation.Kind.PATH_TRANSITION -> applyPathTransition(activeAnimation, node, local)
            UiAnimation.Kind.PARALLEL -> {
                for (child in node.children) applyNode(activeAnimation, child, local, startValues)
            }
            UiAnimation.Kind.SEQUENCE -> {
                var cursor = local
                for (child in node.children) {
                    val childTotal = totalDuration(child)
                    if (cursor >= childTotal) {
                        applyNode(activeAnimation, child, childTotal, startValuesFor(activeAnimation, child))
                        cursor -= childTotal
                    } else {
                        applyNode(activeAnimation, child, cursor, startValuesFor(activeAnimation, child))
                        return
                    }
                }
            }
            UiAnimation.Kind.STAGGER -> {
                var index = 0
                for (child in node.children) {
                    val childElapsed = local - index * node.staggerMs
                    if (childElapsed >= 0.0) {
                        applyNode(activeAnimation, child, minOf(childElapsed, totalDuration(child)), startValues)
                    }
                    index++
                }
            }
            else -> throw IllegalStateException("未知动画类型: ${node.kind}")
        }
    }

    // ===== Transition 插值 =====

    private fun applyTransition(
        activeAnimation: AnimationScheduler.ActiveAnimation,
        node: UiAnimation,
        localMs: Double,
        startValues: MutableMap<String, Any?>,
    ) {
        val raw = if (node.durationMs <= 0.0) 1.0 else localMs / node.durationMs
        val progress = Easing.fromName(node.easing).apply(raw)
        for ((prop, binding) in node.props) {
            if (prop !in activeAnimation.props) continue
            val target = literalValue(binding)
            val start = if (startValues.containsKey(prop)) {
                startValues[prop]
            } else {
                currentValue(activeAnimation.element, prop)
            }
            applyInterpolated(activeAnimation.element, prop, start, target, progress)
        }
    }

    // ===== Keyframe 插值 =====

    private fun applyKeyframes(
        activeAnimation: AnimationScheduler.ActiveAnimation,
        node: UiAnimation,
        localMs: Double,
    ) {
        val keyframes = node.keyframes.toMutableList()
        if (keyframes.isEmpty()) return
        keyframes.sortBy { it.offset }
        val raw = if (node.durationMs <= 0.0) 1.0 else localMs / node.durationMs
        val progress = Easing.fromName(node.easing).apply(raw)
        var prev = keyframes[0]
        var next = keyframes[keyframes.size - 1]
        for (frame in keyframes) {
            if (frame.offset <= progress) prev = frame
            if (frame.offset >= progress) {
                next = frame
                break
            }
        }
        val props: MutableSet<String> = LinkedHashSet()
        props.addAll(prev.props.keys)
        props.addAll(next.props.keys)
        val span = next.offset - prev.offset
        val localProgress = if (span <= 0.0) 1.0 else (progress - prev.offset) / span
        for (prop in props) {
            if (prop !in activeAnimation.props) continue
            val start = if (prev.props.containsKey(prop)) {
                literalValue(prev.props[prop])
            } else {
                currentValue(activeAnimation.element, prop)
            }
            val target = if (next.props.containsKey(prop)) literalValue(next.props[prop]) else start
            applyInterpolated(activeAnimation.element, prop, start, target, localProgress)
        }
    }

    // ===== 路径过渡 =====

    private fun applyPathTransition(
        activeAnimation: AnimationScheduler.ActiveAnimation,
        node: UiAnimation,
        localMs: Double,
    ) {
        val props = node.props
        val raw = if (node.durationMs <= 0.0) 1.0 else localMs / node.durationMs
        val progress = Easing.clamp(Easing.fromName(node.easing).apply(raw))
        val p0 = readPoint(props, "p0x", "p0y") ?: return
        val p1 = readPoint(props, "p1x", "p1y") ?: return
        val p2 = readPoint(props, "p2x", "p2y") ?: return
        val p3 = readPoint(props, "p3x", "p3y")
        val sample = if (p3 != null) {
            Bezier.cubic(p0, p1, p2, p3, progress.toFloat())
        } else {
            Bezier.quadratic(p0, p1, p2, progress.toFloat())
        }
        PropAppliers.apply(activeAnimation.element, "translateX", sample[0].toDouble())
        PropAppliers.apply(activeAnimation.element, "translateY", sample[1].toDouble())
    }

    // ===== 弹簧驱动 =====

    @JvmStatic
    fun applySpringNode(activeAnimation: AnimationScheduler.ActiveAnimation, spring: Spring, dtSec: Double): Boolean {
        val node = activeAnimation.animation
        // applySpringNode 仅在调用方确认 spring 非空后调用（见 AnimationScheduler 弹簧分支门控），
        // spring 转 Kotlin 真可空后此处复刻旧平台类型的非空传递。
        val initialVelocity = node.spring!!.initialVelocity
        var allRest = true
        for ((prop, entry) in node.props) {
            if (prop !in activeAnimation.props) continue
            val target = literalValue(entry)
            val start = activeAnimation.startValues[prop] ?: currentValue(activeAnimation.element, prop)
            val isColor = colorProps.contains(prop) &&
                start != null &&
                target != null &&
                canCoerceColor(start) &&
                canCoerceColor(target)
            val targetPos = if (isColor) 1.0 else numericOf(target)
            val startPos = if (isColor) 0.0 else numericOf(start)
            var state = activeAnimation.springState[prop]
            if (state == null) {
                state = doubleArrayOf(startPos, initialVelocity)
                activeAnimation.springState[prop] = state
            }
            val stepped = spring.step(state[0], state[1], targetPos, dtSec)
            val rest = spring.atRest(stepped.position, stepped.velocity, targetPos)
            if (rest) {
                state[0] = targetPos
                state[1] = 0.0
            } else {
                state[0] = stepped.position
                state[1] = stepped.velocity
                allRest = false
            }
            writeSpringValue(activeAnimation.element, prop, start, target, state[0], isColor)
        }
        return allRest
    }

    private fun writeSpringValue(
        element: UIElement<*>,
        prop: String,
        start: Any?,
        target: Any?,
        position: Double,
        isColor: Boolean,
    ) {
        if (isColor) {
            val a = ValueCoercion.toIntColor(start)
            val b = ValueCoercion.toIntColor(target)
            val t = position.coerceIn(0.0, 1.0)
            PropAppliers.apply(element, prop, lerpColor(a, b, t))
            return
        }
        if (applySilentSliderValue(element, prop, position)) return
        PropAppliers.apply(element, prop, position)
    }

    private fun numericOf(value: Any?): Double = if (value is Number) value.toDouble() else 0.0

    @Suppress("SwallowedException")
    private fun canCoerceColor(value: Any?): Boolean = try {
        ValueCoercion.toIntColor(value)
        true
    } catch (_: RuntimeException) {
        false
    }

    // ===== 插值与颜色混合 =====

    @JvmStatic
    fun applyInterpolated(element: UIElement<*>, prop: String, start: Any?, target: Any?, progress: Double) {
        if (colorProps.contains(prop) && start != null && target != null) {
            try {
                val a = ValueCoercion.toIntColor(start)
                val b = ValueCoercion.toIntColor(target)
                PropAppliers.apply(element, prop, lerpColor(a, b, Easing.clamp(progress)))
                return
            } catch (_: RuntimeException) { }
        }
        if (start is Number && target is Number) {
            val a = start.toDouble()
            val b = target.toDouble()
            val value = a + (b - a) * Easing.clamp(progress)
            if (applySilentSliderValue(element, prop, value)) return
            PropAppliers.apply(element, prop, value)
        } else if (progress >= 1.0) {
            PropAppliers.apply(element, prop, target)
        }
    }

    @JvmStatic
    fun lerpColor(from: Int, to: Int, t: Double): Int {
        val a = lerpChannel(from ushr 24 and 0xFF, to ushr 24 and 0xFF, t)
        val r = lerpChannel(from ushr 16 and 0xFF, to ushr 16 and 0xFF, t)
        val g = lerpChannel(from ushr 8 and 0xFF, to ushr 8 and 0xFF, t)
        val b = lerpChannel(from and 0xFF, to and 0xFF, t)
        return (a shl 24) or (r shl 16) or (g shl 8) or b
    }

    @JvmStatic
    fun lerpChannel(from: Int, to: Int, t: Double): Int {
        val v = mathRoundCompat(from + (to - from) * t).toInt()
        return v.coerceIn(0, 255)
    }

    // ===== 数值静默赋值（Slider / ProgressBar 的 value 动画通道）=====

    /**
     * value 属性的静默写通道：动画中间值只写显示值字段，不触碰目标值、不重触发过渡。
     *
     * - [Slider]：写滑动显示值；
     * - [ProgressBar]：写进度显示值（进度过渡）。
     */
    @JvmStatic
    fun applySilentSliderValue(element: UIElement<*>, prop: String, value: Any?): Boolean {
        if (prop != "value" || value !is Number) return false
        when (element) {
            is Slider -> element.setAnimatedValue(value.toDouble())
            is ProgressBar -> element.setAnimatedValue(value.toDouble())
            else -> return false
        }
        return true
    }

    // ===== 属性读取与工具方法 =====

    @JvmStatic
    fun currentValue(element: UIElement<*>, prop: String): Any? = PropAppliers.read(element, prop)

    @JvmStatic
    fun literalValue(binding: top.wcpe.allincore.dsl.ir.model.UiBinding?): Any? = binding?.literalValue

    @JvmStatic
    fun readPoint(
        props: Map<String, top.wcpe.allincore.dsl.ir.model.UiBinding>,
        xKey: String,
        yKey: String,
    ): FloatArray? {
        val x = props[xKey] ?: return null
        val y = props[yKey] ?: return null
        val xv = x.literalValue ?: return null
        val yv = y.literalValue ?: return null
        if (xv !is Number || yv !is Number) return null
        return floatArrayOf(xv.toFloat(), yv.toFloat())
    }

    // ===== 时长计算与属性收集 =====

    @JvmStatic
    fun totalDuration(node: UiAnimation): Double = when (node.kind) {
        UiAnimation.Kind.TRANSITION, UiAnimation.Kind.KEYFRAME, UiAnimation.Kind.PATH_TRANSITION ->
            node.delayMs + node.durationMs
        UiAnimation.Kind.PARALLEL -> {
            var max = node.durationMs
            for (child in node.children) max = maxOf(max, totalDuration(child))
            node.delayMs + max
        }
        UiAnimation.Kind.SEQUENCE -> {
            var sum = node.durationMs
            for (child in node.children) sum += totalDuration(child)
            node.delayMs + sum
        }
        UiAnimation.Kind.STAGGER -> {
            var staggerMax = node.durationMs
            var index = 0
            for (child in node.children) {
                staggerMax = maxOf(staggerMax, index * node.staggerMs + totalDuration(child))
                index++
            }
            node.delayMs + staggerMax
        }
        else -> throw IllegalStateException("未知动画类型: ${node.kind}")
    }

    @JvmStatic
    fun collectProps(animation: UiAnimation): MutableSet<String> {
        val props: MutableSet<String> = LinkedHashSet()
        if (animation.kind == UiAnimation.Kind.PATH_TRANSITION) {
            props.add("translateX")
            props.add("translateY")
        } else {
            props.addAll(animation.props.keys)
        }
        for (keyframe in animation.keyframes) props.addAll(keyframe.props.keys)
        for (child in animation.children) props.addAll(collectProps(child))
        return props
    }

    // ===== 序列子节点起始值缓存 =====

    @JvmStatic
    fun startValuesFor(
        activeAnimation: AnimationScheduler.ActiveAnimation,
        node: UiAnimation,
    ): MutableMap<String, Any?> = activeAnimation.nodeStartValues.getOrPut(node) {
        val starts: MutableMap<String, Any?> = LinkedHashMap()
        for (prop in collectProps(node)) starts[prop] = currentValue(activeAnimation.element, prop)
        starts
    }
}
